import { z } from 'zod/v4';
import { trpc } from '@org/clients/school';
import { DynamicForm } from '@org/ui';
import { toast } from 'sonner';
import { useState, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { unwrapTrpcData } from '@org/ui';
import { useTranslation } from 'react-i18next';

const journeySchema = z.object({
  journeyId: z.string().optional(),
  name: z.string().min(1, "Name is required").describe('journeys.form.labels.name'),
  description: z
    .string()
    .max(2000, "Description must be 2000 characters or less")
    .describe('journeys.form.labels.description')
    .optional(),
  levelId: z.string().min(1, "Level is required").describe('journeys.form.labels.level'),
});

export interface JourneyFormProps {
  defaultValues: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
  hideButtons?: boolean;
  /** Fires when the fields differ from the last saved values (and when they are back in sync). */
  onDirtyChange?: (dirty: boolean) => void;
}

export interface JourneyFormRef {
  submit: () => void;
}

export const JourneyForm = forwardRef<JourneyFormRef, JourneyFormProps>(({
  defaultValues,
  onSubmit,
  onClose,
  hideButtons,
  onDirtyChange
}, ref) => {
  const utils = trpc.useUtils();
  const journeyId = defaultValues?.journeyId;
  const { data: levels } = trpc.levels.list.useQuery();
  const { t } = useTranslation('admin');

  const [formDefaultValues, setFormDefaultValues] = useState(defaultValues);
  const formRef = useRef<HTMLFormElement>(null);

  const levelOptions = useMemo(
    () => unwrapTrpcData(levels) ?? [],
    [levels]
  );

  const createMutation = trpc.journeys.create.useMutation({
    onSuccess: () => utils.journeys.list.invalidate()
  });

  const updateMutation = trpc.journeys.update.useMutation({
    onSuccess: () => utils.journeys.list.invalidate()
  });

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (formRef.current) {
        formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  }));

  const handleFormSubmit = async (data: z.infer<typeof journeySchema>) => {
    try {
      let resultData = data;

      if (journeyId) {
        await updateMutation.mutateAsync({
          id: journeyId,
          data: {
            name: data.name,
            description: data.description,
            levelId: data.levelId
          }
        });
        toast.success(t('common.toasts.updateSuccess', { entity: 'Journey' }));
        resultData = { ...data, journeyId };
      } else {
        const created = await createMutation.mutateAsync({
          name: data.name,
          description: data.description,
          levelId: data.levelId
        });
        toast.success(t('common.toasts.createSuccess', { entity: 'Journey' }));
        resultData = { ...data, journeyId: created.journeyId };
      }

      // The saved values become the new baseline, so the form reads as clean.
      setFormDefaultValues(resultData);
      onSubmit(resultData);
    } catch (error) {
      console.error("Error saving journey:", error);
      toast.error(t('common.toasts.actionError', { action: 'save', entity: 'journey' }));
    }
  };

  const labelMappings = {
    name: t('journeys.form.labels.name'),
    description: t('journeys.form.labels.description'),
    levelId: t('journeys.form.labels.level')
  };

  const hiddenFields = ['journeyId'];

  const foreignKeyOptions = {
    levels: levelOptions.map((level: any) => ({
      id: level.levelId,
      label: level.name,
    })),
  };

  return (
    <DynamicForm
      schema={journeySchema}
      onSubmit={handleFormSubmit}
      defaultValues={formDefaultValues}
      isEditing={!!journeyId}
      onClose={onClose}
      labelMappings={labelMappings}
      hiddenFields={hiddenFields}
      hideButtons={hideButtons}
      formRef={formRef}
      foreignKeyOptions={foreignKeyOptions}
      onDirtyChange={onDirtyChange}
    />
  );
});
